import { useRef, useCallback } from 'react';

interface UseDragScrollOptions {
  speed?: number; // Multiplier for scroll speed (default: 1)
  dragThreshold?: number; // Minimum pixels to consider it a drag (default: 5)
}

interface DragScrollRef<T extends HTMLElement> {
  /** Use as callback ref: <div ref={scrollRef} /> */
  (el: T | null): void;
  /** Access the DOM element imperatively (e.g., scrollTo) */
  current: T | null;
}

/**
 * Hook to enable drag-to-scroll on horizontally scrollable elements.
 * Works on desktop with mouse drag only — does NOT capture wheel events
 * to avoid blocking page scroll.
 *
 * Uses a callback ref so it works with conditionally rendered elements
 * (e.g., subcategory bars that appear after data loads).
 *
 * IMPORTANT: Prevents click events after dragging to avoid triggering
 * button clicks when user was just scrolling.
 */
export function useDragScroll<T extends HTMLElement>(options: UseDragScrollOptions = {}) {
  const { speed = 1, dragThreshold = 10 } = options;
  const isDragging = useRef(false);
  const hasDragged = useRef(false);
  const startX = useRef(0);
  const scrollLeft = useRef(0);
  const cleanupRef = useRef<(() => void) | null>(null);
  const elRef = useRef<T | null>(null);

  const callbackRef = useCallback((el: T | null) => {
    // Cleanup previous element's listeners
    if (cleanupRef.current) {
      cleanupRef.current();
      cleanupRef.current = null;
    }

    elRef.current = el;
    // Keep .current in sync on the returned function object
    (callbackRef as any).current = el;

    if (!el) return;

    el.style.cursor = 'grab';

    const handleMouseDown = (e: MouseEvent) => {
      isDragging.current = true;
      hasDragged.current = false;
      startX.current = e.pageX - el.offsetLeft;
      scrollLeft.current = el.scrollLeft;
      el.style.cursor = 'grabbing';
      el.style.userSelect = 'none';
    };

    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging.current) return;
      e.preventDefault();
      const x = e.pageX - el.offsetLeft;
      const walk = (x - startX.current) * speed;
      el.scrollLeft = scrollLeft.current - walk;
      // Only treat this as a drag (which suppresses the upcoming click) when
      // the pointer moved past the threshold AND the strip actually scrolled.
      // The old code flagged a drag on any >5px pointer drift, so a normal
      // click that jittered a few px — or a click on a rail with nothing to
      // scroll — got its click killed, swallowing legitimate category /
      // subcategory navigations. Requiring real scroll movement fixes that.
      if (
        Math.abs(x - startX.current) > dragThreshold &&
        Math.abs(el.scrollLeft - scrollLeft.current) > 2
      ) {
        hasDragged.current = true;
      }
    };

    const handleMouseUp = () => {
      if (!isDragging.current) return;
      isDragging.current = false;
      el.style.cursor = 'grab';
      el.style.userSelect = '';
    };

    const handleClick = (e: MouseEvent) => {
      if (hasDragged.current) {
        e.preventDefault();
        e.stopPropagation();
        hasDragged.current = false;
      }
    };

    el.addEventListener('mousedown', handleMouseDown);
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    el.addEventListener('click', handleClick, { capture: true });

    cleanupRef.current = () => {
      el.removeEventListener('mousedown', handleMouseDown);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      el.removeEventListener('click', handleClick, { capture: true } as EventListenerOptions);
    };
  }, [speed, dragThreshold]) as DragScrollRef<T>;

  // Initialize .current on the function object
  callbackRef.current = elRef.current;

  return callbackRef;
}
